"use client";

/**
 * Report section and group headings, with the optional "View all →" control.
 *
 * Extracted from the design source `compass-oppreport.jsx` (`.rv-h2`,
 * `.rv-grouphead`, `.rv-eyebrow`, `.rv-viewlink`).
 *
 * Per plan §9 #4, `onViewAll` scrolls to another report section — it is not an
 * assumptions-page link. The composition decides where it goes.
 */

import { ArrowR } from "../lib/icons";

export interface ReportSectionHeadingProps {
  /** `.rv-h2` when `level` is "section", `.rv-eyebrow` when "group". */
  title: string;
  level?: "section" | "group";
  /** Renders the `.rv-viewlink` "View all →" control. */
  onViewAll?: () => void;
  viewAllLabel?: string;
}

/** `.rv-viewlink` — 12px semibold navy link with a trailing arrow. */
function ViewAllLink({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={[
        "inline-flex items-center gap-[5px] shrink-0",
        "font-[family-name:var(--font-body)] text-[12px] font-semibold",
        "text-(--fg-brand) cursor-pointer py-0.5",
        "hover:underline hover:underline-offset-2",
      ].join(" ")}
      onClick={onClick}
    >
      {label}
      <ArrowR size={12} />
    </button>
  );
}

/**
 * A report heading. `"section"` renders the 19px `.rv-h2` document heading;
 * `"group"` renders the uppercase `.rv-eyebrow` inside a `.rv-grouphead` row.
 */
export function ReportSectionHeading({
  title,
  level = "section",
  onViewAll,
  viewAllLabel = "View all",
}: ReportSectionHeadingProps) {
  if (level === "group") {
    return (
      <div className="flex items-center justify-between mt-[22px] mb-2.5">
        <span
          className={[
            "inline-flex items-center gap-1.5",
            "text-[10.5px] tracking-[0.1em] uppercase",
            "font-bold text-(--fg-muted)",
          ].join(" ")}
        >
          {title}
        </span>
        {onViewAll && <ViewAllLink label={viewAllLabel} onClick={onViewAll} />}
      </div>
    );
  }

  return (
    <div className="flex items-baseline justify-between gap-3 mb-4">
      {/* The `!` is load-bearing: `tokens.css` styles bare `h2` outside any
          cascade layer (`h2 { font-size: 14px; line-height: 20px }`), and an
          unlayered element selector beats every Tailwind utility, which all
          live in `@layer utilities`. Without it this renders at 14px. */}
      <h2
        className={[
          "m-0 text-[19px]! font-bold leading-[1.3]! tracking-[-0.02em]!",
          "text-(--fg-primary)",
        ].join(" ")}
      >
        {title}
      </h2>
      {onViewAll && <ViewAllLink label={viewAllLabel} onClick={onViewAll} />}
    </div>
  );
}
